import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, ChevronDown, Filter, Loader2, ChevronLeft, ChevronRight, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const ENTITY_COLORS = {
  student: 'bg-blue-100 text-blue-700',
  teacher: 'bg-teal-100 text-teal-700',
  fee: 'bg-yellow-100 text-yellow-700',
  exam: 'bg-purple-100 text-purple-700',
  class: 'bg-indigo-100 text-indigo-700',
  behavior_incident: 'bg-red-100 text-red-700',
  lesson_note: 'bg-emerald-100 text-emerald-700',
  default: 'bg-slate-100 text-slate-600',
};

function EntityBadge({ entity }) {
  const cls = ENTITY_COLORS[entity] || ENTITY_COLORS.default;
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${cls}`}>
      {entity?.replace('_', ' ') || 'system'}
    </span>
  );
}

export default function AuditLogsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filterEntity) params.set('entity', filterEntity);
      if (filterAction) params.set('action', filterAction);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await api.get(`/analytics/audit-logs?${params.toString()}`);
      if (res.success) setData(res.data);
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, filterEntity, filterAction, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const logs = data?.logs || [];

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-NG', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="audit-logs-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-blue-600" />
          Audit Logs
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Complete trail of admin and system actions for your school
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 flex flex-wrap gap-3 items-end">
        <Filter className="w-4 h-4 text-slate-400 self-center" />

        {/* Entity filter */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Entity</label>
          <div className="relative">
            <select
              value={filterEntity}
              onChange={e => { setFilterEntity(e.target.value); setPage(1); }}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-7 min-w-[140px]"
              data-testid="audit-entity-filter"
            >
              <option value="">All Entities</option>
              {(data?.entities || []).map(e => (
                <option key={e} value={e}>{e.replace('_', ' ')}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Action search */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Search Action</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              value={filterAction}
              onChange={e => { setFilterAction(e.target.value); setPage(1); }}
              placeholder="e.g. Added student"
              className="border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm bg-white outline-none min-w-[180px]"
              data-testid="audit-action-search"
            />
          </div>
        </div>

        {/* Date range */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none"
            data-testid="audit-date-from"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => { setDateTo(e.target.value); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none"
            data-testid="audit-date-to"
          />
        </div>

        <button
          onClick={() => { setFilterEntity(''); setFilterAction(''); setDateFrom(''); setDateTo(''); setPage(1); }}
          className="text-xs text-slate-500 hover:text-slate-700 self-end pb-2 ml-1 underline"
          data-testid="audit-clear-filters"
        >
          Clear
        </button>

        <div className="ml-auto self-end text-sm text-slate-400">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `${data?.total || 0} entries`}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">Time</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">User</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">Action</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">Entity</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading && !logs.length && (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td colSpan={5} className="px-4 py-3">
                      <div className="h-4 bg-slate-100 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              )}
              {!loading && logs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                    No audit logs found for the selected filters.
                  </td>
                </tr>
              )}
              {logs.map((log, i) => (
                <tr key={log.id || i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {formatDate(log.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-slate-700">{log.user_name || 'System'}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{log.action}</td>
                  <td className="px-4 py-3">
                    <EntityBadge entity={log.entity} />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-[300px] truncate" title={log.message}>
                    {log.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              Page {data.page} of {data.pages} ({data.total} total)
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="audit-prev-page"
              >
                <ChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <button
                disabled={page >= data.pages}
                onClick={() => setPage(p => p + 1)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="audit-next-page"
              >
                <ChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
